import type { EmailCodeSent, LoginInput, StartImpersonationInput, TwoFactorMethod } from '@chukua/common';
import { api } from '../../shared.api';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  isActive: boolean;
  totpEnabled: boolean;
  emailOtpEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  /** The union of every role the user holds. Server remains the authority — this is UX only (CLAUDE.md → Web conventions). */
  permissions: string[];
  /** Present only while an admin is acting as this user (testing only, docs: admin impersonation). */
  impersonation?: { impersonatorId: string; impersonatorName: string };
}

/** The account has a second factor on: ask for a code from one of `methods`, the first one first. */
export interface LoginStepUpRequired {
  requiresTwoFactor: true;
  methods: TwoFactorMethod[];
}

/** An account owing a forced password change (an admin reset it) gets this instead of a user — the same request, no session yet. */
export interface PasswordChangeRequired {
  passwordChangeRequired: true;
}

/** An account with a second factor on gets `requiresTwoFactor` instead of a user — the same request, no session yet. */
export function login(input: LoginInput): Promise<CurrentUser | LoginStepUpRequired | PasswordChangeRequired> {
  return api<CurrentUser | LoginStepUpRequired | PasswordChangeRequired>('/auth/login', { method: 'POST', body: JSON.stringify(input) });
}

/** A 6-digit code is checked against `method`; a recovery code works whichever method is showing. */
export function verifyTwoFactorLogin(code: string, method: TwoFactorMethod): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/two-factor/verify', { method: 'POST', body: JSON.stringify({ code, method }) });
}

/** Emails a code for the login in progress; also "Send another code". A test deployment returns the code instead. */
export function sendLoginEmailCode(): Promise<EmailCodeSent> {
  return api<EmailCodeSent>('/auth/two-factor/email-code', { method: 'POST' });
}

/** Completes a forced password change; lands the account on the same next step a fresh login would (a two-factor challenge, or straight to a session). */
export function completePasswordChange(password: string): Promise<CurrentUser | LoginStepUpRequired> {
  return api<CurrentUser | LoginStepUpRequired>('/auth/password/complete', { method: 'POST', body: JSON.stringify({ password }) });
}

export function logout(): Promise<void> {
  return api<void>('/auth/logout', { method: 'POST' });
}

export function me(): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/me');
}

export function startImpersonation(input: StartImpersonationInput): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/impersonation', { method: 'POST', body: JSON.stringify(input) });
}

export function stopImpersonation(): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/impersonation', { method: 'DELETE' });
}
